import { Browser, createElement, detach, isNullOrUndefined, print as printWindow } from '@syncfusion/ej2-base';
import { BlockType, CommandName } from '../../models/enums';
import { decoupleReference, getBlockContentElement, getBlockModelById, getInlineToolbarItems, getSelectedRange, hasActiveTableSelection, isListTypeBlock, setCursorPosition, setSelectionRange } from '../../common/utils/index';
import { convertHtmlElementToBlocks, getBlockDataAsHTML } from '../../common/utils/html-parser';
import * as constants from '../../common/constant';
import { BlockFactory } from '../services/index';
export class BlockEditorMethods {
    constructor(manager) {
        this.parent = manager;
    }
    addBlock(block, targetId, isAfter, preventUIUpdate) {
        const targetBlockModel = getBlockModelById(targetId, this.parent.getEditorBlocks());
        const populatedBlock = BlockFactory.populateBlockProperties([block], targetBlockModel ? targetBlockModel.parentId : '');
        this.parent.execCommand({
            command: 'AddBlock',
            state: {
                block: populatedBlock[0],
                targetBlock: this.parent.blockContainer.querySelector(`#${targetId}`),
                isAfter: isAfter,
                preventUIUpdate: preventUIUpdate
            }
        });
    }
    removeBlock(blockId) {
        const blockElement = this.parent.blockContainer.querySelector(`#${blockId}`);
        this.parent.execCommand({
            command: 'DeleteBlock',
            state: {
                blockElement: blockElement,
                isMethod: true,
                isUndoRedoAction: false,
                preventMinimumOne: true
            }
        });
    }
    getBlock(blockId) {
        return getBlockModelById(blockId, this.parent.getEditorBlocks());
    }
    moveBlock(fromBlockId, toBlockId) {
        this.parent.execCommand({
            command: 'MoveBlock',
            state: {
                fromBlockIds: [fromBlockId],
                toBlockId: toBlockId,
                isInteracted: false
            }
        });
    }
    updateBlock(blockId, properties) {
        if (!blockId || !properties) {
            return false;
        }
        const block = this.getBlock(blockId);
        if (!block) {
            return false;
        }
        /* Model Updates */
        this.parent.blockService.updateBlock(blockId, properties);
        this.parent.stateManager.updateManagerBlocks();
        /* UI Updates */
        const updatedBlockModel = this.getBlock(blockId);
        const oldBlockElement = this.parent.getBlockElementById(blockId);
        const newBlockElement = this.parent.blockRenderer.createBlockElement(updatedBlockModel);
        const parentBlock = getBlockModelById(updatedBlockModel.parentId, this.parent.getEditorBlocks());
        let wrapper = this.parent.blockContainer;
        if (parentBlock) {
            const parentBlockElement = this.parent.getBlockElementById(parentBlock.id);
            let selector = '';
            switch (parentBlock.blockType) {
                case BlockType.Callout:
                    selector = '.' + constants.CALLOUT_CONTENT_CLS;
                    break;
                case BlockType.Quote:
                    selector = '.' + constants.QUOTE_CONTENT_CLS;
                    break;
                default:
                    if (parentBlock.blockType.toString().startsWith('Collapsible')) {
                        selector = '.' + constants.TOGGLE_CONTENT_CLS;
                    }
                    break;
            }
            if (selector) {
                wrapper = parentBlockElement.querySelector(selector);
            }
        }
        if (wrapper) {
            wrapper.insertBefore(newBlockElement, oldBlockElement);
            detach(oldBlockElement);
        }
        if (isListTypeBlock(updatedBlockModel.blockType)) {
            this.parent.listPlugin.recalculateMarkersForListItems();
            if (block.blockType === BlockType.Checklist) {
                if (this.parent.blockRenderer.listRenderer) {
                    this.parent.blockRenderer.listRenderer.toggleCheckedState(updatedBlockModel, updatedBlockModel.properties.isChecked);
                }
            }
        }
        return true;
    }
    executeToolbarAction(command, value) {
        const builtInCommands = Object.keys(CommandName);
        if (builtInCommands.indexOf(command) !== -1) {
            const convertedCommand = command.toLowerCase();
            this.parent.formattingAction.execCommand({ command: convertedCommand, value: value });
        }
    }
    setSelection(node, start, end) {
        if (node) {
            setSelectionRange(node, start, end);
        }
    }
    setCursorPosition(blockId, position) {
        const blockElement = this.parent.getBlockElementById(blockId);
        if (blockElement) {
            const contentElement = getBlockContentElement(blockElement);
            setCursorPosition(contentElement, position);
        }
    }
    getSelectedBlocks() {
        const range = this.getRange();
        if (!range) {
            return null;
        }
        const tableBlk = this.parent.currentFocusedBlock &&
            this.parent.currentFocusedBlock.closest(`.${constants.TABLE_BLOCK_CLS}`);
        if (tableBlk && hasActiveTableSelection(tableBlk)) {
            return this.parent.tableSelectionManager.getSelectedCellBlocks(tableBlk);
        }
        const selectedBlocks = [];
        const editorBlocks = this.parent.getEditorBlocks();
        const blockElements = this.parent.blockContainer.querySelectorAll('.' + constants.BLOCK_CLS);
        const parent = range.commonAncestorContainer;
        const element = parent.nodeType === Node.ELEMENT_NODE
            ? parent
            : parent.parentElement;
        const isSelectionInsideChild = !!(element && element.closest(`.${constants.CALLOUT_CONTENT_CLS}, .${constants.QUOTE_CONTENT_CLS}, .${constants.TOGGLE_CONTENT_CLS}, .${constants.TABLE_CELL_BLK_CONTAINER}`));
        blockElements.forEach((blockElement) => {
            const blockRange = document.createRange();
            blockRange.selectNodeContents(blockElement);
            const block = getBlockModelById(blockElement.id, editorBlocks);
            const isChildrenRootParent = isSelectionInsideChild &&
                (blockElement.classList.contains(constants.CALLOUT_BLOCK_CLS) ||
                    blockElement.classList.contains(constants.QUOTE_BLOCK_CLS) ||
                    blockElement.classList.contains(constants.TOGGLE_BLOCK_CLS) ||
                    blockElement.classList.contains(constants.TABLE_BLOCK_CLS));
            const tableRootBlk = blockElement.closest(`.${constants.TABLE_BLOCK_CLS}`);
            const isSelectionInsideTableCell = this.parent.currentFocusedBlock &&
                !isNullOrUndefined(this.parent.currentFocusedBlock.closest('.e-cell-blocks-container'));
            const canAllowTableBlks = (!tableRootBlk || blockElement.classList.contains('e-table-block')
                || isSelectionInsideTableCell);
            if (block && range.intersectsNode(blockElement) && !isChildrenRootParent && canAllowTableBlks) {
                selectedBlocks.push(block);
            }
        });
        return selectedBlocks;
    }
    getRange() {
        return getSelectedRange();
    }
    selectRange(range) {
        const selection = window.getSelection();
        if (selection) {
            selection.removeAllRanges();
            selection.addRange(range);
        }
    }
    selectBlock(blockId) {
        const blockElement = this.parent.getBlockElementById(blockId);
        if (blockElement) {
            const range = document.createRange();
            range.selectNodeContents(blockElement);
            this.selectRange(range);
        }
    }
    selectAllBlocks() {
        const range = document.createRange();
        range.selectNodeContents(this.parent.blockContainer);
        this.selectRange(range);
    }
    focusIn() {
        const startBlock = this.parent.getEditorBlocks()[0];
        const startBlkEle = this.parent.getBlockElementById(startBlock.id);
        if (startBlkEle) {
            this.parent.setFocusAndUIForNewBlock(startBlkEle);
        }
    }
    focusOut() {
        this.parent.removeFocusAndUIForBlock(this.parent.currentFocusedBlock);
        if (this.parent.blockContainer) {
            this.parent.blockContainer.blur();
            const selection = window.getSelection();
            selection.removeAllRanges();
        }
    }
    getBlockCount() {
        return this.parent.blocks.length;
    }
    enableDisableToolbarItems(itemId, enable) {
        const toolbarPopup = document.querySelector('#' + this.parent.rootEditorElement.id + constants.INLINE_TBAR_POPUP_ID);
        const ids = typeof itemId === 'string' ? [itemId] : itemId;
        const parentToolbarElements = [];
        const tbarItemModels = [];
        const items = this.parent.inlineToolbarSettings.items;
        ids.forEach((id) => {
            items.forEach((it) => {
                if (typeof it === 'string') {
                    if (it.toLowerCase() === id.toLowerCase()) {
                        const defaults = getInlineToolbarItems();
                        const match = defaults.find((d) => d.command && d.command.toLowerCase() === it.toLowerCase());
                        if (match) {
                            tbarItemModels.push(match);
                        }
                    }
                }
                else {
                    if (it.id === id || it.command === id) {
                        tbarItemModels.push(it);
                    }
                }
            });
        });
        tbarItemModels.forEach((item) => {
            if (!item || !item.command) {
                return;
            }
            const element = toolbarPopup.querySelector(`[data-command=${item.command}]`);
            if (element) {
                parentToolbarElements.push(element);
            }
        });
        if (parentToolbarElements.length > 0) {
            this.parent.observer.notify('enableDisableTbarItems', {
                items: parentToolbarElements,
                isEnable: enable
            });
        }
    }
    getDataAsJson(blockId) {
        if (blockId) {
            const block = getBlockModelById(blockId, this.parent.getEditorBlocks());
            return block ? decoupleReference(block) : null;
        }
        else {
            return this.parent.getEditorBlocks().map((block) => decoupleReference(block));
        }
    }
    getDataAsHtml(blockId) {
        if (blockId) {
            const block = getBlockModelById(blockId, this.parent.getEditorBlocks());
            return block ? getBlockDataAsHTML([block], this.parent.rootEditorElement.id) : null;
        }
        else {
            return getBlockDataAsHTML(this.parent.getEditorBlocks(), this.parent.rootEditorElement.id);
        }
    }
    parseHtmlToBlocks(html) {
        const container = createElement('div', { innerHTML: html });
        return convertHtmlElementToBlocks(container, true);
    }
    renderBlocksFromJson(json, replace, targetBlockId) {
        try {
            const blocksJson = typeof json === 'string' ? JSON.parse(json) : json;
            const blocks = this.extractBlocks(blocksJson);
            const sanitizedBlocks = blocks.map((block) => decoupleReference(block));
            this.parent.stateManager.populateUniqueIds(sanitizedBlocks);
            const populatedBlocks = BlockFactory.populateBlockProperties(sanitizedBlocks);
            if (replace) {
                return this.replaceAllBlocks(populatedBlocks);
            }
            else {
                return this.insertBlocksAtPosition(populatedBlocks, targetBlockId);
            }
        }
        catch (e) {
            console.error('Error rendering blocks from JSON:', e);
            return false;
        }
    }
    extractBlocks(blocksJson) {
        let blocks = [];
        if (Array.isArray(blocksJson)) {
            blocks = blocksJson;
        }
        else if (blocksJson && typeof blocksJson === 'object') {
            if (Array.isArray(blocksJson.blocks)) {
                blocks = blocksJson.blocks;
            }
            else {
                // Try to convert single object to block if it looks like a block
                if (blocksJson.blockType) {
                    blocks = [blocksJson];
                }
            }
        }
        return blocks;
    }
    /**
     * Replaces all blocks in the editor with the provided blocks.
     *
     * @param {BlockModel[]} blocks - The blocks to render
     * @returns {boolean} - True if operation was successful, false otherwise
     * @hidden
     */
    replaceAllBlocks(blocks) {
        this.parent.setEditorBlocks([]);
        this.parent.blockContainer.innerHTML = '';
        if (blocks.length === 0) {
            this.parent.blockCommand.createDefaultEmptyBlock(true);
            return true;
        }
        this.parent.setEditorBlocks(blocks);
        this.parent.stateManager.updateManagerBlocks();
        this.parent.blockRenderer.renderBlocks(this.parent.getEditorBlocks());
        return true;
    }
    /**
     * Inserts blocks at a specific position in the editor.
     *
     * @param {BlockModel[]} blocks - The blocks to insert
     * @param {string} targetBlockId - ID of the block to insert after, uses focused block if not provided
     * @returns {boolean} - True if operation was successful, false otherwise
     * @private
     */
    insertBlocksAtPosition(blocks, targetBlockId) {
        if (blocks.length === 0) {
            return false;
        }
        let insertionPointId = targetBlockId;
        if (!insertionPointId) {
            if (this.parent.currentFocusedBlock) {
                insertionPointId = this.parent.currentFocusedBlock.id;
            }
            else {
                insertionPointId = this.parent.getEditorBlocks()[this.parent.getEditorBlocks().length - 1].id;
                const blockElement = this.parent.getBlockElementById(insertionPointId);
                this.parent.setFocusToBlock(blockElement);
            }
        }
        let lastInsertedElement;
        for (let i = 0; i < blocks.length; i++) {
            const block = blocks[i];
            const targetId = i === 0 ? insertionPointId : lastInsertedElement.id;
            const addedBlock = this.parent.blockCommand.addBlock({
                block,
                targetBlock: this.parent.getBlockElementById(targetId),
                isAfter: true,
                preventEventTrigger: true
            });
            lastInsertedElement = this.parent.getBlockElementById(addedBlock.id);
        }
        if (lastInsertedElement) {
            const contentElement = getBlockContentElement(lastInsertedElement);
            this.parent.setFocusToBlock(lastInsertedElement);
            setCursorPosition(contentElement, contentElement.innerText.length);
        }
        return true;
    }
    print() {
        const blockHtml = getBlockDataAsHTML(this.parent.blocks, this.parent.rootEditorElement.id);
        const tempDiv = createElement('div');
        tempDiv.innerHTML = blockHtml;
        let printWind = window.open('', 'print', 'height=' + window.outerHeight + ',width=' + window.outerWidth);
        if (Browser.info.name === 'msie') {
            printWind.resizeTo(screen.availWidth, screen.availHeight);
        }
        printWind = printWindow(tempDiv, printWind);
    }
}
